import * as pc from 'playcanvas';
import { mountAnalytics } from './analytics';
import { mountPostMessage } from './post-message';
import { mountUi } from './ui';
import { resolveEmbed } from './resolve-embed';

const slug = new URL(window.location.href).pathname.split('/').pop() ?? '';

async function boot() {
  const fallback = document.getElementById('fallback');
  try {
    const embed = await resolveEmbed(slug);
    if (!embed) {
      throw new Error('Embed niet gevonden');
    }
    if (embed.expired) {
      if (fallback) fallback.textContent = 'Deze 3D-tour is niet meer beschikbaar.';
      return;
    }
    if (!embed.assets.sog_url) {
      if (fallback) fallback.textContent = 'Verwerking nog niet voltooid.';
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.id = 'pc-canvas';
    document.getElementById('app')!.appendChild(canvas);

    const app = new pc.AppBase(canvas);
    // WebGL2-only: WebGPU geeft op veel Windows/headless setups een null-adapter → createDevice-crash.
    const device = await pc.createGraphicsDevice(canvas, { deviceTypes: ['webgl2'], antialias: true });

    // pc.AppBase vereist expliciete componentSystems + resourceHandlers in AppOptions.
    const opts = new pc.AppOptions();
    opts.graphicsDevice = device;
    opts.mouse = new pc.Mouse(canvas);
    opts.touch = new pc.TouchDevice(canvas);
    opts.keyboard = new pc.Keyboard(window);
    opts.componentSystems = [
      pc.RenderComponentSystem,
      pc.CameraComponentSystem,
      pc.LightComponentSystem,
      pc.ScriptComponentSystem,
      pc.GSplatComponentSystem,
    ];
    opts.resourceHandlers = [
      pc.TextureHandler,
      pc.ContainerHandler,
      pc.ScriptHandler,
      pc.GSplatHandler,
    ];
    app.init(opts);
    app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
    app.setCanvasResolution(pc.RESOLUTION_AUTO);

    // Camera + orbit-controls rond het midden van de ruimte.
    const camera = new pc.Entity('camera');
    camera.addComponent('camera', {
      clearColor: new pc.Color(0.05, 0.06, 0.08),
      fov: 75,
    });
    app.root.addChild(camera);

    const target = new pc.Vec3(0, 1.3, 0);
    let yaw = 180;
    let pitch = -8;
    let dist = 3.4;
    const tmp = new pc.Vec3();
    function updateCam() {
      const ry = (yaw * Math.PI) / 180;
      const rp = (pitch * Math.PI) / 180;
      const x = target.x + dist * Math.cos(rp) * Math.sin(ry);
      const y = target.y + dist * Math.sin(rp);
      const z = target.z + dist * Math.cos(rp) * Math.cos(ry);
      camera.setPosition(x, y, z);
      camera.lookAt(target, tmp.set(0, 1, 0));
    }
    updateCam();

    let dragging = false;
    let lastX = 0;
    let lastY = 0;
    canvas.addEventListener('pointerdown', (e) => { dragging = true; lastX = e.clientX; lastY = e.clientY; });
    window.addEventListener('pointerup', () => { dragging = false; });
    window.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      yaw -= (e.clientX - lastX) * 0.3;
      pitch = Math.max(-80, Math.min(80, pitch + (e.clientY - lastY) * 0.3));
      lastX = e.clientX;
      lastY = e.clientY;
      updateCam();
    });
    canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      dist = Math.max(0.1, Math.min(200, dist * (e.deltaY > 0 ? 1.15 : 0.87)));
      updateCam();
    }, { passive: false });

    // Auto-frame: camera op het midden + afstand van de échte model-bounds (COLMAP-schaal varieert).
    function frameToSplat(entity: pc.Entity): boolean {
      try {
        const res = splatAsset.resource as unknown as { aabb?: pc.BoundingBox } | undefined;
        const inst = (entity as unknown as { gsplat?: { instance?: { aabb?: pc.BoundingBox; meshInstance?: { aabb?: pc.BoundingBox } } } }).gsplat?.instance;
        const aabb = res?.aabb ?? inst?.aabb ?? inst?.meshInstance?.aabb;
        if (!aabb || !aabb.halfExtents) return false;
        const r = aabb.halfExtents.length();
        if (!(r > 0) || !isFinite(r)) return false;
        target.copy(aabb.center);
        dist = r * 2.2;
        pitch = -10;
        yaw = 180;
        updateCam();
        return true;
      } catch {
        return false;
      }
    }

    // Gaussian Splat asset
    const splatAsset = new pc.Asset(
      'scene-sog',
      'gsplat',
      { url: embed.assets.sog_url, filename: 'scene.sog' },
    );
    app.assets.add(splatAsset);
    splatAsset.on('load', () => {
      const splat = new pc.Entity('splat');
      splat.addComponent('gsplat', { asset: splatAsset });
      app.root.addChild(splat);
      if (fallback) fallback.remove();
      // resource.aabb is direct beschikbaar; retry kort als instance-bounds nog moeten settelen.
      if (!frameToSplat(splat)) {
        let tries = 0;
        const tryFrame = () => {
          if (frameToSplat(splat) || tries++ > 10) return;
          setTimeout(tryFrame, 150);
        };
        setTimeout(tryFrame, 80);
      }
    });
    splatAsset.on('error', (err: unknown) => {
      console.error('splat load failed', err);
      if (fallback) fallback.textContent = 'Kan 3D-scan niet laden.';
    });
    app.assets.load(splatAsset);

    app.start();

    mountUi(embed);
    mountAnalytics(embed);
    mountPostMessage();
  } catch (err) {
    console.error('viewer boot failed', err);
    if (fallback) fallback.textContent = 'Fout bij laden.';
  }
}

void boot();
